import { ref, computed } from 'vue'
import { useCurrentHost } from '@/composables/useCurrentHost'
import { CANONICAL_HOSTS } from '@/config/hosts'
import { useBrandStore } from '@/stores/brand'

interface ResolvedShop {
  shopId: string
  shopCode: string
  shopName: string
  isSubdomain: boolean
  isCustomDomain: boolean
}

// Session storage key for persisting resolved shop
const RESOLVED_SHOP_KEY = 'zaveato_resolved_shop'

// Try to restore from sessionStorage on module load.
//
// SRP note: this runs at MODULE LOAD (before any Vue setup context), so
// we can't use `useCurrentHost` here — composables require a component
// scope. The raw `window.location.hostname` read is a plain cache-key
// match, not classification, so it doesn't violate the single-source-of-
// truth rule for hostname parsing.
function getInitialResolvedShop(): ResolvedShop | null {
  try {
    const stored = sessionStorage.getItem(RESOLVED_SHOP_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed._hostname === window.location.hostname) {
        return parsed.shop
      }
    }
  } catch {
    // Ignore errors
  }
  return null
}

// Cached resolved shop to avoid repeated API calls
const resolvedShop = ref<ResolvedShop | null>(getInitialResolvedShop())
const isResolving = ref(false)
const resolutionError = ref<string | null>(null)

/**
 * Composable for detecting and resolving shops from custom domains/subdomains.
 *
 * When the frontend is loaded on a custom domain (e.g., mybusiness.staging.zaveato.co.uk)
 * or a custom domain (e.g., mybusiness.com), this composable detects it and resolves
 * the shop from the backend.
 */
export function useShopDomain() {
  /**
   * Get the base domain from environment or default
   */
  const baseDomain = computed(() => {
    // In production: zaveato.co.uk
    // In staging: staging.zaveato.co.uk
    // In development: localhost
    return import.meta.env.VITE_BASE_DOMAIN || 'zaveato.co.uk'
  })

  // Hostname parsing is delegated to `useCurrentHost` (SRP: single owner
  // of window.location parsing). This composable only layers the
  // shop-domain-specific rules (baseDomain suffix, reserved subdomains)
  // on top.
  const { hostname: currentHostname, isLocalDev } = useCurrentHost()

  const RESERVED_SUBDOMAINS = ['www', 'api', 'staging', 'admin', 'app', 'mail', 'ftp', 'dev', 'test']

  /**
   * Check if the current hostname is a custom subdomain or custom domain
   */
  const isCustomDomain = computed(() => {
    if (typeof window === 'undefined') return false

    const hostname = currentHostname.value

    // Localhost + .local dev hosts are never a custom domain (RFC 6762
    // reserves .local for local networks — safe to always treat as non-WL).
    if (isLocalDev.value) return false

    // Canonical Zaveato hosts (all regions + staging siblings, e.g.
    // zaveato.co.uk, staging.zaveato.co.uk, pk-staging.zaveato.co.uk, ...)
    // are never a custom domain. Single source of truth = CANONICAL_HOSTS
    // from src/config/hosts.ts; adding a new region entry there lights up
    // this check automatically. Do NOT re-list hosts here — that was the
    // bug that made pk-staging.zaveato.co.uk fall through to WL resolution.
    if (CANONICAL_HOSTS.has(hostname)) return false

    // If it ends with base domain but isn't a canonical host, it's a
    // per-shop subdomain (e.g. acme.zaveato.co.uk or acme.staging.zaveato.co.uk).
    if (hostname.endsWith(`.${baseDomain.value}`)) {
      const subdomain = hostname.replace(`.${baseDomain.value}`, '')
      if (!RESERVED_SUBDOMAINS.includes(subdomain) && !subdomain.includes('.')) {
        return true
      }
      return false
    }

    // Any other domain is a WL custom domain (customer-registered, e.g.
    // burnagebutchers.co.uk).
    return true
  })

  /**
   * Get the subdomain from the current hostname (if applicable)
   */
  const subdomain = computed(() => {
    if (typeof window === 'undefined') return null

    const hostname = currentHostname.value
    const suffix = `.${baseDomain.value}`

    if (hostname.endsWith(suffix)) {
      const sub = hostname.replace(suffix, '')
      if (!RESERVED_SUBDOMAINS.includes(sub) && !sub.includes('.')) {
        return sub
      }
    }

    return null
  })

  /**
   * Resolve the shop from the current hostname.
   *
   * REFACTORED (Sep 2026): now a thin wrapper over the brand store — no
   * independent API call. Previously this composable and the brand store
   * each hit their own resolution endpoint, races between them collapsed
   * WL routes into `/404`. Single source of truth is now the brand store;
   * `bootstrap()` runs on module load and again in the router guard.
   *
   * Callers still get the same ResolvedShop shape back, cached in
   * sessionStorage keyed by hostname (survives full page reloads).
   */
  async function resolveShop(): Promise<ResolvedShop | null> {
    if (!isCustomDomain.value) return null
    if (resolvedShop.value) return resolvedShop.value

    isResolving.value = true
    resolutionError.value = null

    try {
      const brandStore = useBrandStore()
      await brandStore.bootstrap()
      const b = brandStore.brand
      if (!b) return null

      const shop: ResolvedShop = {
        shopId: b.shopId,
        shopCode: b.shopCode,
        shopName: b.shopName,
        isSubdomain: false,
        isCustomDomain: true,
      }
      resolvedShop.value = shop

      try {
        sessionStorage.setItem(RESOLVED_SHOP_KEY, JSON.stringify({
          _hostname: currentHostname.value,
          shop,
        }))
      } catch { /* private mode / quota — safe to ignore */ }

      return shop
    } catch (error: any) {
      resolutionError.value = error?.message || 'Failed to resolve shop'
      return null
    } finally {
      isResolving.value = false
    }
  }

  /**
   * Clear the cached resolved shop
   */
  function clearResolvedShop() {
    resolvedShop.value = null
    resolutionError.value = null
  }

  /**
   * Get the shop code for the current domain (either from resolution or URL)
   */
  async function getShopCode(): Promise<string | null> {
    const shop = await resolveShop()
    return shop?.shopCode || null
  }

  // `resolvedShop` MUST reflect the brand store live, without needing a
  // separate `resolveShop()` call. The refactor (Sep 2026) removed the
  // router's independent API call — so if this computed only read the
  // local ref, consumers like ProductDetailView / useStorefrontShop that
  // just READ `resolvedShop.value` (no `resolveShop()` invocation) got
  // null on WL and rendered "Shop not found" for every deep-link.
  // Bridging to the brand store here keeps the single source of truth
  // while preserving the ResolvedShop shape 9 callers already expect.
  const resolvedShopComputed = computed<ResolvedShop | null>(() => {
    if (resolvedShop.value) return resolvedShop.value
    const brandStore = useBrandStore()
    const b = brandStore.brand
    if (!b) return null
    return {
      shopId: b.shopId,
      shopCode: b.shopCode,
      shopName: b.shopName,
      isSubdomain: false,
      isCustomDomain: true,
    }
  })

  return {
    baseDomain,
    isCustomDomain,
    subdomain,
    resolvedShop: resolvedShopComputed,
    isResolving: computed(() => isResolving.value),
    resolutionError: computed(() => resolutionError.value),
    resolveShop,
    clearResolvedShop,
    getShopCode
  }
}

export default useShopDomain
